import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import getMotion, {
  type MotionConfigSource,
  type MotionDownloadType,
} from '@cartwhl/common/api/getMotion';

export const AXEL_CHARACTER_ID = 'char-upload-Axel';

type MotionRequestResult = Awaited<ReturnType<typeof getMotion>>;
type MotionResponseData = MotionRequestResult['data'];
type MotionResponseError = MotionRequestResult['error'];

type PreviewCacheEntry = {
  motionUrl: string | null;
  lastRetargetedStateRevisionKey: string | number | null;
};

const previewCache = new Map<string, PreviewCacheEntry>();

/** Drop cached retarget preview URLs (call when leaving Cartographer). */
export function clearCartographerMotionRetargetPreviewCache() {
  previewCache.clear();
}

interface UseCartographerMotionRetargetPreviewOptions {
  enabled: boolean;
  apiKey: string;
  motionID: string | null;
  characterID: string;
  /** Zero-based body index for multi-person comic jobs. */
  bodyIndex?: number;
  configSource?: MotionConfigSource;
  downloadType?: MotionDownloadType;
  fps: number;
  frameStep: number;
  rootMotion: boolean;
  autoRefreshDebounceMs?: number;
  refreshKey?: string | number | null;
  stateRevisionKey?: string | number | null;
  resetOnDisable?: boolean;
  prepareForUserMotionRequest?: (options: {
    configSource: MotionConfigSource;
    mode: 'auto' | 'manual';
  }) => Promise<boolean> | boolean;
}

interface UseCartographerMotionRetargetPreviewResult {
  motionUrl: string | null;
  loading: boolean;
  mainError: string | null;
  lastSuccessfulRefreshToken: number;
  previewOutOfDate: boolean;
  refreshNow: (options?: { configSource?: MotionConfigSource }) => void;
}

function readCompletedMotionUrl(
  data: MotionResponseData,
  error: MotionResponseError,
  downloadType: MotionDownloadType,
): { url: string | null; message: string | null } {
  if (error || !data) {
    return { url: null, message: error?.message ?? 'Failed to load motion' };
  }

  const url =
    downloadType === 'gltf'
      ? data.gltfURL
      : downloadType === 'fbx'
        ? data.fbxURL
        : downloadType === 'cwms'
          ? data.cwmsURL
          : data.bvhURL;

  if (data.status !== 'COMPLETED' || !url) {
    return {
      url: null,
      message:
        data.status !== 'COMPLETED'
          ? `Motion not ready (status: ${data.status})`
          : `No ${downloadType.toUpperCase()} URL in response`,
    };
  }

  return { url, message: null };
}

function buildPreviewCacheKey(options: {
  characterID: string;
  motionID: string | null;
  bodyIndex: number | undefined;
  fps: number;
  frameStep: number;
  rootMotion: boolean;
  configSource: MotionConfigSource;
  downloadType: MotionDownloadType;
}): string | null {
  const {
    characterID,
    motionID,
    bodyIndex,
    fps,
    frameStep,
    rootMotion,
    configSource,
    downloadType,
  } = options;
  if (!characterID || !motionID) return null;

  return `${characterID}|${motionID}|${bodyIndex ?? 0}|${configSource}|${downloadType}|${fps}|${frameStep}|${
    rootMotion ? '1' : '0'
  }`;
}

export default function useCartographerMotionRetargetPreview({
  enabled,
  apiKey,
  motionID,
  characterID,
  bodyIndex,
  configSource = 'saved',
  downloadType = 'bvh',
  fps,
  frameStep,
  rootMotion,
  autoRefreshDebounceMs = 0,
  refreshKey,
  stateRevisionKey = null,
  resetOnDisable = true,
  prepareForUserMotionRequest,
}: UseCartographerMotionRetargetPreviewOptions): UseCartographerMotionRetargetPreviewResult {
  const [motionUrl, setMotionUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mainError, setMainError] = useState<string | null>(null);
  const [lastSuccessfulRefreshToken, setLastSuccessfulRefreshToken] = useState(0);
  const [lastRetargetedStateRevisionKey, setLastRetargetedStateRevisionKey] =
    useState<string | number | null>(null);
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const motionUrlRef = useRef<string | null>(null);

  useEffect(() => {
    motionUrlRef.current = motionUrl;
  }, [motionUrl]);

  const resetPreviewState = useCallback(() => {
    setLoading(false);
    setMainError(null);
    setMotionUrl(null);
    setLastRetargetedStateRevisionKey(null);
  }, []);

  const previewCacheKey = useMemo(
    () =>
      buildPreviewCacheKey({
        characterID,
        motionID,
        bodyIndex,
        configSource,
        downloadType,
        fps,
        frameStep,
        rootMotion,
      }),
    [
      bodyIndex,
      characterID,
      configSource,
      downloadType,
      fps,
      frameStep,
      motionID,
      rootMotion,
    ],
  );

  const cachedLastRetargetedStateRevisionKey = previewCacheKey
    ? (previewCache.get(previewCacheKey)?.lastRetargetedStateRevisionKey ?? null)
    : null;
  const effectiveLastRetargetedStateRevisionKey =
    lastRetargetedStateRevisionKey ?? cachedLastRetargetedStateRevisionKey;
  const previewOutOfDate =
    stateRevisionKey != null &&
    effectiveLastRetargetedStateRevisionKey != null &&
    `${effectiveLastRetargetedStateRevisionKey}` !== `${stateRevisionKey}`;

  const loadPreview = useCallback(
    async (
      mode: 'auto' | 'manual',
      overrideConfigSource?: MotionConfigSource,
    ) => {
      const effectiveConfigSource = overrideConfigSource ?? configSource;
      const effectivePreviewCacheKey = buildPreviewCacheKey({
        characterID,
        motionID,
        bodyIndex,
        configSource: effectiveConfigSource,
        downloadType,
        fps,
        frameStep,
        rootMotion,
      });

      if (
        !enabled ||
        !apiKey ||
        !characterID ||
        !motionID ||
        !effectivePreviewCacheKey
      ) {
        return;
      }

      const cachedPreview = previewCache.get(effectivePreviewCacheKey) ?? null;
      const currentMotionUrl = motionUrlRef.current ?? cachedPreview?.motionUrl ?? null;
      const shouldLoadMotion = mode === 'manual' || !currentMotionUrl;
      if (!shouldLoadMotion) return;

      const requestSeq = ++requestSeqRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setMainError(null);
      if (!currentMotionUrl) {
        setMotionUrl(null);
      }

      try {
        if (prepareForUserMotionRequest) {
          const didPrepare = await prepareForUserMotionRequest({
            configSource: effectiveConfigSource,
            mode,
          });
          if (!didPrepare) {
            return;
          }
        }

        const { data, error } = await getMotion(
          motionID,
          apiKey,
          {
            characterID,
            ...(effectiveConfigSource === 'temp'
              ? { configSource: effectiveConfigSource }
              : {}),
            downloadType,
            fps,
            frameStep,
            rootMotion,
            ...(bodyIndex !== undefined ? { bodyIndex } : {}),
          },
          controller.signal,
        );

        if (requestSeq !== requestSeqRef.current || controller.signal.aborted) return;

        const completedMotion = readCompletedMotionUrl(data, error, downloadType);
        if (!completedMotion.url) {
          setMainError(
            completedMotion.message ?? 'Failed to load motion for this character',
          );
          if (!currentMotionUrl) {
            setMotionUrl(null);
          }
          return;
        }

        const nextLastRetargetedStateRevisionKey =
          stateRevisionKey ?? cachedPreview?.lastRetargetedStateRevisionKey ?? null;

        setMotionUrl(completedMotion.url);
        setLastRetargetedStateRevisionKey(nextLastRetargetedStateRevisionKey);
        previewCache.set(effectivePreviewCacheKey, {
          motionUrl: completedMotion.url,
          lastRetargetedStateRevisionKey: nextLastRetargetedStateRevisionKey,
        });
        setLastSuccessfulRefreshToken((token) => token + 1);
      } catch (loadError) {
        if (requestSeq !== requestSeqRef.current || controller.signal.aborted) return;
        setMainError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load motion preview',
        );
        if (!currentMotionUrl) {
          setMotionUrl(null);
        }
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [
      apiKey,
      characterID,
      configSource,
      downloadType,
      enabled,
      fps,
      frameStep,
      motionID,
      bodyIndex,
      prepareForUserMotionRequest,
      rootMotion,
      stateRevisionKey,
    ],
  );

  useEffect(
    () => () => {
      requestSeqRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
    },
    [],
  );

  useEffect(() => {
    requestSeqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;

    if (!previewCacheKey) {
      resetPreviewState();
      return;
    }

    const cachedPreview = previewCache.get(previewCacheKey) ?? null;
    setMotionUrl(cachedPreview?.motionUrl ?? null);
    setLastRetargetedStateRevisionKey(
      cachedPreview?.lastRetargetedStateRevisionKey ?? null,
    );
    setLoading(false);
    setMainError(null);
  }, [previewCacheKey, resetOnDisable, resetPreviewState]);

  useEffect(() => {
    if (!apiKey || !characterID || !motionID) {
      requestSeqRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
      resetPreviewState();
      return;
    }

    if (!enabled) {
      requestSeqRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
      if (resetOnDisable) {
        resetPreviewState();
      } else {
        setLoading(false);
        setMainError(null);
      }
      return;
    }

    const cachedPreview = previewCacheKey ? previewCache.get(previewCacheKey) ?? null : null;
    const cachedMotionPreview = cachedPreview?.motionUrl ?? null;

    if (cachedMotionPreview && !motionUrlRef.current) {
      motionUrlRef.current = cachedMotionPreview;
      setMotionUrl(cachedMotionPreview);
      setLoading(false);
      setMainError(null);
    }

    if (
      cachedPreview?.lastRetargetedStateRevisionKey != null &&
      lastRetargetedStateRevisionKey == null
    ) {
      setLastRetargetedStateRevisionKey(
        cachedPreview.lastRetargetedStateRevisionKey,
      );
    }

    const hasPreview = Boolean(motionUrlRef.current ?? cachedMotionPreview);
    if (previewOutOfDate && hasPreview) return;
    if (hasPreview) return;

    if (autoRefreshDebounceMs > 0) {
      const timeoutId = window.setTimeout(() => {
        void loadPreview('auto');
      }, autoRefreshDebounceMs);
      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    void loadPreview('auto');
  }, [
    apiKey,
    autoRefreshDebounceMs,
    characterID,
    enabled,
    lastRetargetedStateRevisionKey,
    loadPreview,
    motionID,
    previewCacheKey,
    previewOutOfDate,
    refreshKey,
    resetOnDisable,
    resetPreviewState,
  ]);

  const refreshNow = useCallback(
    (options?: { configSource?: MotionConfigSource }) => {
      void loadPreview('manual', options?.configSource);
    },
    [loadPreview],
  );

  return {
    motionUrl,
    loading,
    mainError,
    lastSuccessfulRefreshToken,
    previewOutOfDate,
    refreshNow,
  };
}
